/**
 * Copy for the Client Admin Twilio modal's credential save
 * (`POST /organizations/{orgId}/sms/twilio-credentials`).
 *
 * Kept free of the axios import so it can be unit-tested on its own.
 */

export interface SmsReadiness {
  can_send_remi_sms?: boolean;
  delivery_status?: string;
  missing?: string[];
  allowed_recipient_countries?: string[];
}

export interface TwilioCredentialsResponse {
  success: boolean;
  account_sid: string;
  account_status?: string;
  friendly_name?: string;
  sms_activation: {
    activated: boolean;
    skipped: boolean;
    reason: string | null;
    readiness: SmsReadiness | null;
  };
}

export interface SmsActivationCopy {
  activated: boolean;
  message: string;
}

const TWILIO_REJECTED = 'Twilio rejected the SID/token';

const PENDING_REASON_COPY: Record<string, string> = {
  no_messaging_service: 'Create a Messaging Service in Twilio and add this number',
  preferred_number_not_in_any_service: 'Add this phone number to the Messaging Service',
  twilio_auth_failed: TWILIO_REJECTED,
};

const PENDING_FALLBACK = 'Saved. SMS will activate after the next daily check';

export const describeSmsActivation = (
  activation: TwilioCredentialsResponse['sms_activation'] | undefined
): SmsActivationCopy => {
  if (activation?.activated) {
    const countries = activation.readiness?.allowed_recipient_countries ?? [];
    const canadaOnly = countries.length === 1 && countries[0] === 'CA';
    return {
      activated: true,
      message: canadaOnly ? 'SMS is active (Canada numbers only)' : 'SMS is active',
    };
  }
  const reason = activation?.reason ?? '';
  return { activated: false, message: PENDING_REASON_COPY[reason] ?? PENDING_FALLBACK };
};

interface ApiErrorLike {
  response?: { data?: { detail?: unknown } };
}

/**
 * Turns a failed credential save into a message the admin can act on.
 * `twilio_auth_failed` gets fixed copy; other structured errors surface the
 * backend's `detail.message`.
 */
export const twilioCredentialsErrorMessage = (error: unknown): string => {
  const detail = (error as ApiErrorLike | undefined)?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const { code, message } = detail as { code?: unknown; message?: unknown };
    if (code === 'twilio_auth_failed') return TWILIO_REJECTED;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (typeof detail === 'string' && detail.trim()) return detail;
  return 'Could not save the Twilio SID/token. Try again.';
};
